/**
 * Preset household scenarios for the ACV vs RCV calculator.
 * Mid-market estimates, not appraisals.
 */

import type { CalcItem } from "./acvRcv";

export interface HouseholdPreset {
  id: string;
  label: string;
  blurb: string;
  items: CalcItem[];
}

const i = (
  id: string,
  name: string,
  categoryId: string,
  rcv: number,
  ageYears: number,
  qty = 1,
): CalcItem => ({ id, name, categoryId, rcv, ageYears, qty });

export const PRESET_HOUSEHOLDS: HouseholdPreset[] = [
  {
    id: "starter",
    label: "Starter apartment",
    blurb: "1-bedroom rental, mostly newer furnishings",
    items: [
      i("s1", "Sofa", "sofa", 1200, 3),
      i("s2", "Mattress + frame", "mattress", 900, 3),
      i("s3", 'TV (55")', "tv", 600, 2),
      i("s4", "Laptop", "laptop", 1100, 3),
      i("s5", "Smartphone", "phone", 900, 2),
      i("s6", "Small appliances", "small_appliance", 500, 4),
      i("s7", "Cookware", "cookware", 350, 4),
      i("s8", "Clothing", "clothing_adult", 2200, 3),
      i("s9", "Books / media", "books", 250, 5),
      i("s10", "Bicycle", "bicycle", 700, 4),
    ],
  },
  {
    id: "modest",
    label: "Modest household",
    blurb: "Family of 3, average furnishings",
    items: [
      i("m1", "Sectional sofa", "sofa", 2800, 6),
      i("m2", "Bedroom set (king)", "bedroom_set", 3200, 8),
      i("m3", "Dining set", "dining_set", 1400, 7),
      i("m4", 'TV (65")', "tv", 1100, 4),
      i("m5", "Laptop", "laptop", 1400, 5),
      i("m6", "Refrigerator", "refrigerator", 1800, 7),
      i("m7", "Washer + Dryer", "washer_dryer", 2200, 7),
      i("m8", "Dishwasher", "dishwasher", 800, 6),
      i("m9", "Microwave + small appliances", "small_appliance", 900, 5),
      i("m10", "Cookware", "cookware", 600, 6),
      i("m11", "Clothing (2 adults)", "clothing_adult", 4000, 4),
      i("m12", "Clothing (1 child)", "clothing_kids", 1200, 2),
      i("m13", "Kids' toys + gear", "toys", 1500, 3),
      i("m14", "Power tools", "power_tools", 2400, 6),
      i("m15", "Bicycle", "bicycle", 1800, 4),
    ],
  },
  {
    id: "comfortable",
    label: "Comfortable home",
    blurb: "Family of 4, nicer furnishings & electronics",
    items: [
      i("c1", "Sectional sofa", "sofa", 4500, 5),
      i("c2", "Accent chairs", "sofa", 1800, 4, 2),
      i("c3", "Primary bedroom set", "bedroom_set", 5500, 7),
      i("c4", "Kids bedroom sets", "bedroom_set", 2400, 5, 2),
      i("c5", "Dining set", "dining_set", 3200, 6),
      i("c6", "Area rugs", "rug", 1200, 5, 3),
      i("c7", 'TV (75")', "tv", 2200, 3),
      i("c8", "TV (secondary)", "tv", 700, 5),
      i("c9", "Laptops", "laptop", 1600, 3, 2),
      i("c10", "Tablets", "tablet", 600, 3, 2),
      i("c11", "Smartphones", "phone", 1000, 2, 2),
      i("c12", "Audio system", "audio", 1500, 5),
      i("c13", "Refrigerator", "refrigerator", 2800, 6),
      i("c14", "Washer + Dryer", "washer_dryer", 3000, 6),
      i("c15", "Range / Oven", "range", 2200, 6),
      i("c16", "Dishwasher", "dishwasher", 1100, 5),
      i("c17", "Small appliances", "small_appliance", 1500, 4),
      i("c18", "Cookware / kitchenware", "cookware", 1800, 6),
      i("c19", "Clothing (2 adults)", "clothing_adult", 7000, 4),
      i("c20", "Clothing (2 kids)", "clothing_kids", 2400, 2),
      i("c21", "Kids' toys + gear", "toys", 2500, 3),
      i("c22", "Power tools", "power_tools", 3500, 7),
      i("c23", "Bicycles", "bicycle", 2200, 4, 2),
      i("c24", "Sports equipment", "sports_gear", 1800, 5),
      i("c25", "Jewelry", "jewelry", 6000, 8),
    ],
  },
  {
    id: "highend",
    label: "High-end household",
    blurb: "Larger home, premium furnishings",
    items: [
      i("h1", "Designer sectional", "sofa", 9000, 4),
      i("h2", "Accent chairs", "sofa", 3500, 3, 2),
      i("h3", "Primary bedroom set", "bedroom_set", 12000, 5),
      i("h4", "Guest / kids bedrooms", "bedroom_set", 4500, 5, 3),
      i("h5", "Dining set", "dining_set", 7500, 5),
      i("h6", "Area rugs", "rug", 3500, 5, 3),
      i("h7", 'TV (85")', "tv", 4000, 3),
      i("h8", "Laptops", "laptop", 2500, 2, 3),
      i("h9", "Smartphones", "phone", 1100, 2, 3),
      i("h10", "Tablets", "tablet", 800, 3, 2),
      i("h11", "Camera kit", "camera", 3500, 4),
      i("h12", "Audio / home theater", "audio", 6000, 5),
      i("h13", "Refrigerator (built-in)", "refrigerator", 6500, 5),
      i("h14", "Washer + Dryer", "washer_dryer", 4500, 5),
      i("h15", "Range / double oven", "range", 5500, 5),
      i("h16", "Dishwasher", "dishwasher", 1800, 4),
      i("h17", "Small appliances", "small_appliance", 3000, 4),
      i("h18", "Cookware / kitchenware", "cookware", 4500, 5),
      i("h19", "Clothing (2 adults)", "clothing_adult", 18000, 4),
      i("h20", "Clothing (kids)", "clothing_kids", 4500, 2, 2),
      i("h21", "Power tools", "power_tools", 5500, 6),
      i("h22", "Bicycles", "bicycle", 4500, 3, 2),
      i("h23", "Sports / recreation", "sports_gear", 4500, 5),
      i("h24", "Musical instrument", "musical_instrument", 6000, 10),
      i("h25", "Jewelry", "jewelry", 20000, 8),
      i("h26", "Fine art", "fine_art", 12000, 10),
    ],
  },
];

export function getPreset(id: string): HouseholdPreset {
  return PRESET_HOUSEHOLDS.find((p) => p.id === id) ?? PRESET_HOUSEHOLDS[1];
}
